import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { Loader, Tooltip } from '../../components';
import BackButton from '../../components/back-button/BackButton';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import { InfoSVG } from '../../components/svgs';
import StripeKycDocumentModal from '../../components/account-settings/payout-info/StripeKycDocumentModal';
import StripePayoutReviewModal from '../../components/account-settings/payout-info/StripePayoutReviewModal';
import StripePayoutSetupModal from '../../components/account-settings/payout-info/StripePayoutSetupModal';
import { InventoryLocationState, pickInventoryListState } from './Inventory';
import { Animal, SpecimenInventory } from '../../types/interfaces';
import { Genders, StripeMarketplaceAccess, StripePayoutSetupMode, TransactionStatusEnum } from '../../types/enums';
import { MarketPlaceRequest, MarketPlaceSpecimenResponse } from '../../types/interfaces/marketPlace.interfaces';
import { StripeAccountStatus } from '../../types/interfaces/stripeConnect.interfaces';
import { getAnimalById } from '../../api/animalsApi';
import { getMarketplaceCommissionById } from '../../api/marketplaceCommissionApi';
import { addToMarketplace, getMarketplaceSpecimens } from '../../api/marketPlaceSpecimenApi';
import { getStripeAccountStatus } from '../../api/stripeConnectApi';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { getStripeMarketplaceAccess, stripeStatusGetInfoBanner } from '../../services/stripeConnect.service';
import { StripePayoutBadgeVariant } from '../../types/enums';
import useCanManagePayouts from '../../hooks/useCanManagePayouts';
import { showToast } from '../../services/toast.service';
import { centsToDollars, dollarsToCents } from '../../services/account.service';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';

const MARKETPLACE_COMMISSION_ID = 1;

type MarketplaceItem = {
  availableQuantity: number;
  isSelected: boolean;
  quantity: number;
  specimenId: number;
  embryoStrawId?: number;
  pricePerUnit?: number | string;
};

const InventoryMarketPlace = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as InventoryLocationState | null;
  const listNavigationState = pickInventoryListState(location.state);
  const marketplaceRequiresAchSetup = locationState?.marketplaceRequiresAchSetup === true;
  const marketplaceRequiresDocumentUpload = locationState?.marketplaceRequiresDocumentUpload === true;
  const marketplacePayoutPending = locationState?.marketplacePayoutPending === true;
  const initialStripeBlocker: Exclude<StripeMarketplaceAccess, StripeMarketplaceAccess.Enabled> | null = marketplacePayoutPending
    ? StripeMarketplaceAccess.Pending
    : marketplaceRequiresDocumentUpload
      ? StripeMarketplaceAccess.Document
      : marketplaceRequiresAchSetup
        ? StripeMarketplaceAccess.Setup
        : null;

  const returnToInventoryItem = () => {
    if (animalId) {
      navigate(`${ROUTE_PATHS.APP_INVENTORY}/${animalId}`, { state: { listNavigationState } });
    } else {
      navigate(ROUTE_PATHS.APP_INVENTORY, { state: listNavigationState });
    }
  };

  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const canManagePayouts = useCanManagePayouts();
  const user = useAppSelector(state => state.user);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ownership, setOwnership] = useState<SpecimenInventory[]>();
  const [selectedMarketplaceItems, setSelectedMarketplaceItems] = useState<MarketplaceItem[]>([]);
  const [totalAvailableOwnedQty, setTotalAvailableOwnedQty] = useState<number>();
  const [animal, setAnimal] = useState<Animal>();
  const [marketplaceExisting, setMarketplaceExisting] = useState<MarketPlaceSpecimenResponse[]>([]);
  const [isAddPaymentMethodModalOpen, setIsAddPaymentMethodModalOpen] = useState<boolean>(marketplaceRequiresAchSetup);
  const [setupModalMode, setSetupModalMode] = useState<StripePayoutSetupMode>(StripePayoutSetupMode.Kyc);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(marketplaceRequiresDocumentUpload);
  const [isPayoutReviewModalOpen, setIsPayoutReviewModalOpen] = useState<boolean>(marketplacePayoutPending);
  const [hasHandledStripePrompt, setHasHandledStripePrompt] = useState<boolean>(false);
  const [stripeAccessChecked, setStripeAccessChecked] = useState<boolean>(false);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [stripeBlocker, setStripeBlocker] = useState<Exclude<StripeMarketplaceAccess, StripeMarketplaceAccess.Enabled> | null>(
    initialStripeBlocker,
  );
  const [commissionPercent, setCommissionPercent] = useState<number | null>(null);
  const TABLE_COLSPAN_BY_GENDER = {
    FEMALE: 10,
    MALE: 8,
  };

  useEffect(() => {
    if (animalId) {
      fetchAnimal();
    } else {
      setAnimal(undefined);
    }
  }, [animalId]);

  useEffect(() => {
    let active = true;
    getMarketplaceCommissionById(MARKETPLACE_COMMISSION_ID)
      .then(response => {
        if (!active) return;
        const value = response.data?.commissionValue;
        setCommissionPercent(typeof value === 'number' ? value : null);
      })
      .catch(() => {
        if (active) setCommissionPercent(null);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (hasHandledStripePrompt) return;
    if (marketplaceRequiresAchSetup) setIsAddPaymentMethodModalOpen(true);
    if (marketplaceRequiresDocumentUpload) setIsDocumentModalOpen(true);
    if (marketplaceRequiresAchSetup || marketplaceRequiresDocumentUpload || marketplacePayoutPending) {
      setHasHandledStripePrompt(true);
    }
  }, [hasHandledStripePrompt, marketplacePayoutPending, marketplaceRequiresAchSetup, marketplaceRequiresDocumentUpload]);

  useEffect(() => {
    const refreshStripeAccess = async () => {
      if (!accountId) {
        setStripeAccessChecked(true);
        return;
      }

      try {
        const [access, statusRes] = await Promise.all([
          getStripeMarketplaceAccess(accountId),
          getStripeAccountStatus(accountId).catch(() => ({ data: null })),
        ]);
        setStripeStatus(statusRes?.data ?? null);
        setStripeBlocker(access === StripeMarketplaceAccess.Enabled ? null : access);
      } catch {
        setStripeStatus(null);
        setStripeBlocker(current => current ?? StripeMarketplaceAccess.Setup);
      } finally {
        setStripeAccessChecked(true);
      }
    };

    void refreshStripeAccess();
  }, [accountId, isAddPaymentMethodModalOpen, isDocumentModalOpen]);

  const fetchAnimal = async () => {
    try {
      const response = await getAnimalById(animalId ?? '');
      setAnimal(response.data);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const isFemale = animal?.gender === Genders.Female;

  const totalMarketplaceQuantity = selectedMarketplaceItems
    .filter(item => item.isSelected)
    .reduce((accumulator, current) => accumulator + current.quantity, 0);

  const getMarketplaceItemTotal = (marketplaceItem?: MarketplaceItem): number => {
    if (!marketplaceItem?.isSelected) return 0;
    const rawPrice = marketplaceItem.pricePerUnit;
    const pricePerUnit = typeof rawPrice === 'string' ? Number.parseFloat(rawPrice) : rawPrice ?? 0;
    if (Number.isNaN(pricePerUnit)) return 0;

    const totalQuantity = isFemale ? marketplaceItem.availableQuantity : marketplaceItem.quantity;
    return totalQuantity * pricePerUnit;
  };

  const totalMarketplaceValue = selectedMarketplaceItems
    .filter(item => item.isSelected)
    .reduce((acc, item) => acc + getMarketplaceItemTotal(item), 0);

  useEffect(() => {
    const handleGetSpecimens = async () => {
      if (!animalId || !accountId || !stripeAccessChecked || stripeBlocker != null) {
        if (stripeAccessChecked) setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const params = {
          include: 'Animal, SpecimenType, EmbryoStraw, SpecimenCustomDataValue',
          userId: user.userId,
          getUpdatedAvailableQuantities: true,
          excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
        };

        const { data } = await getSpecimensByOwnership(animalId, accountId, params);

        const flattenedOwnership: SpecimenInventory[] = isFemale
          ? data.flatMap((specimen: SpecimenInventory) =>
              specimen.embryoStraws?.length
                ? specimen.embryoStraws.map(straw => ({
                    ...specimen,
                    embryoStrawCode: straw.embryoStrawCode,
                    embryoStrawNumber: straw.embryoStrawNumber,
                    embryoStrawId: straw.embryoStrawId,
                    quantityAvailable: straw.embryoCountPerStraw ?? 0,
                    embryoGrade: straw.embryoGrade,
                    embryoStage: straw.embryoStage,
                  }))
                : specimen,
            )
          : data;

        setOwnership(flattenedOwnership);
        setTotalAvailableOwnedQty(flattenedOwnership.reduce((acc, curr) => acc + curr.quantityAvailable, 0));

        const ownershipRowsAlignedWithTable = flattenedOwnership.filter((item: SpecimenInventory) => {
          if (isFemale && !item.embryoStrawId) return false;
          return true;
        });

        let existingData: MarketPlaceSpecimenResponse[] = [];

        if (!isFemale && accountId && animalId) {
          try {
            const { data } = await getMarketplaceSpecimens({
              filter: `AnimalId eq ${animalId};IsDeleted eq false`,
            });
            existingData = data;
            setMarketplaceExisting(data);
          } catch (error) {
            showToast.error(toastMessages.SOMETHING_WENT_WRONG);
          }
        }
        const marketplaceItems = ownershipRowsAlignedWithTable.map(item => {
          const match = existingData.find(marketplaceItem => marketplaceItem.specimenId === item.specimenId);

          return {
            isSelected: false,
            specimenId: item.specimenId,
            embryoStrawId: item.embryoStrawId,
            quantity: 0,
            availableQuantity: item.quantityAvailable,
            pricePerUnit: match ? match.unitPriceInCents / 100 : 0,
          };
        });

        setSelectedMarketplaceItems(marketplaceItems);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    handleGetSpecimens();
  }, [animalId, accountId, stripeAccessChecked, stripeBlocker, isFemale, user.userId]);

  const isValid = (isSubmit?: boolean) => {
    if (!selectedMarketplaceItems.find((marketplaceItem: MarketplaceItem) => marketplaceItem.isSelected)) {
      isSubmit && showToast.error(toastMessages.NO_ITEM_SELECTED);
      return false;
    } else if (
      selectedMarketplaceItems.some(
        marketplaceItem => marketplaceItem.isSelected && marketplaceItem.quantity > marketplaceItem.availableQuantity,
      )
    ) {
      isSubmit && showToast.error(toastMessages.INVALID_MARKETPLACE_QUANTITY);
      return false;
    } else if (!totalAvailableOwnedQty || totalMarketplaceQuantity > totalAvailableOwnedQty) {
      isSubmit && showToast.error(toastMessages.INVALID_MARKETPLACE_QUANTITY);
      return false;
    } else if (
      totalMarketplaceQuantity === 0 ||
      selectedMarketplaceItems.some(marketplaceItem => marketplaceItem.isSelected && marketplaceItem.quantity === 0)
    ) {
      isSubmit && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    }
    const hasInvalidPrice = selectedMarketplaceItems
      .filter(marketplaceItem => marketplaceItem.isSelected)
      .some(marketplaceItem => {
        const raw = marketplaceItem.pricePerUnit;
        const price = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number.parseFloat(raw) : NaN;

        return Number.isNaN(price) || price <= 0;
      });

    if (hasInvalidPrice) {
      isSubmit && showToast.error(toastMessages.PRICE_REQUIRED);
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (stripeBlocker === StripeMarketplaceAccess.Document) {
      setIsDocumentModalOpen(true);
      return;
    }
    if (stripeBlocker === StripeMarketplaceAccess.Restricted) {
      if (!canManagePayouts) {
        showToast.error(MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_OWNER_ACTION_REQUIRED);
        return;
      }
      setSetupModalMode(StripePayoutSetupMode.Edit);
      setIsAddPaymentMethodModalOpen(true);
      showToast.error(LABEL_CONSTANTS.STRIPE_RESTRICTED_BANNER_MESSAGE);
      return;
    }
    if (stripeBlocker === StripeMarketplaceAccess.Pending) {
      setIsPayoutReviewModalOpen(true);
      return;
    }
    if (stripeBlocker === StripeMarketplaceAccess.Setup) {
      setSetupModalMode(StripePayoutSetupMode.Kyc);
      setIsAddPaymentMethodModalOpen(true);
      showToast.error(toastMessages.PAYMENT_DETAILS_REQUIRED);
      return;
    }
    if (isValid(true)) {
      try {
        setIsLoading(true);
        let newSelectedMarketplaceItems = selectedMarketplaceItems.filter(item => item.isSelected);
        const mappedMarketplaceSpecimens: MarketPlaceRequest[] = newSelectedMarketplaceItems.map(item => {
          const priceNumber = dollarsToCents(isFemale ? getMarketplaceItemTotal(item) : Number(item.pricePerUnit) || 0);

          const request: MarketPlaceRequest = {
            animalId: Number(animalId),
            specimenId: item.specimenId,
            quantity: item.quantity,
            unitPriceInCents: priceNumber,
          };

          if (isFemale && item.embryoStrawId) {
            request.selectedEmbryoStrawIds = [item.embryoStrawId];
          }

          return request;
        });

        if (!accountId) {
          throw new Error('Account Id is not defined');
        }
        await addToMarketplace(accountId, mappedMarketplaceSpecimens);
        showToast.success(toastMessages.ADD_TO_MARKETPLACE_SUCCESS);
        returnToInventoryItem();
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const inputCheckbox = (marketplaceItem: MarketplaceItem | undefined, itemIndex: number) => {
    return (
      <input
        type="checkbox"
        name="selected-checkin-specimen-row"
        onChange={e =>
          setSelectedMarketplaceItems(prev => {
            const newItems = [...prev];
            if (marketplaceItem && itemIndex >= 0) {
              const updatedItem = { ...marketplaceItem, isSelected: e.target.checked };
              if (e.target.checked) {
                if (isFemale) {
                  updatedItem.quantity = marketplaceItem.availableQuantity;
                }
              } else {
                updatedItem.quantity = 0;
                const existingMatch = marketplaceExisting.find(
                  existingMarketplaceItem => existingMarketplaceItem.specimenId === marketplaceItem?.specimenId,
                );
                updatedItem.pricePerUnit = existingMatch ? centsToDollars(existingMatch.unitPriceInCents) : 0;
              }
              newItems[itemIndex] = updatedItem;
            }
            return newItems;
          })
        }
        checked={marketplaceItem?.isSelected || false}
      />
    );
  };

  const inputQuantityElement = (marketplaceItem: MarketplaceItem | undefined, itemIndex: number, row?: SpecimenInventory) => {
    let displayValue: string | number = '-';

    if (isFemale) {
      displayValue = marketplaceItem?.availableQuantity ?? '-';
    } else if (marketplaceItem?.isSelected) {
      displayValue = marketplaceItem.quantity > 0 ? marketplaceItem.quantity : '-';
    }

    return (
      <input
        type="text"
        placeholder="0"
        value={displayValue}
        disabled={isFemale || !marketplaceItem?.isSelected}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (isFemale || itemIndex < 0) return;
          const count = +e.target.value.replace(/[^\d]+/, '').replace(/^0+/, '');
          let newQuantities = [...selectedMarketplaceItems];
          newQuantities[itemIndex] = { ...selectedMarketplaceItems[itemIndex], quantity: count };
          setSelectedMarketplaceItems(newQuantities);
        }}
      />
    );
  };

  const inputPriceElement = (
    marketplaceItem: MarketplaceItem | undefined,
    itemIndex: number,
    existingMatch?: MarketPlaceSpecimenResponse,
  ) => {
    const displayValue = marketplaceItem?.pricePerUnit ? marketplaceItem.pricePerUnit : '';

    return (
      <input
        type="text"
        inputMode="decimal"
        placeholder="0"
        value={displayValue}
        disabled={!marketplaceItem?.isSelected || (!!existingMatch && !isFemale)}
        onChange={e => {
          if (itemIndex < 0) return;
          const raw = e.target.value;
          if (raw !== '' && !/^\d*(?:\.\d{0,2})?$/.test(raw)) return;
          const price = raw === '' ? undefined : raw;
          setSelectedMarketplaceItems(prev => {
            const updated = [...prev];
            updated[itemIndex] = { ...updated[itemIndex], pricePerUnit: price };
            return updated;
          });
        }}
      />
    );
  };

  const handleErrors = (marketplaceItem: MarketplaceItem | undefined, item: SpecimenInventory): '' | 'error' | 'warning' => {
    if (!marketplaceItem?.isSelected) return '';
    const quantity = marketplaceItem.quantity ?? 0;
    if (quantity > item.quantityAvailable) return 'error';
    if (quantity === 0) return 'warning';
    return '';
  };

  return (
    <div className="inventory-marketplace">
      <BackButton onClick={returnToInventoryItem} />
      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.ADD_TO_MARKETPLACE}</h1>
        {isLoading ? (
          <Loader loaderSize="medium" simple />
        ) : (
          <form action="submit" onSubmit={e => handleSubmit(e)}>
            <h2>{ANIMAL_CONSTANTS.ANIMAL_INFO}</h2>
            <p className="animal-info-text">
              {ANIMAL_CONSTANTS.ANIMAL_CODE}: {ownership?.length ? ownership[0].animal?.code : ''}
            </p>
            <p className="animal-info-text">
              {ANIMAL_CONSTANTS.ANIMAL_NAME}: {ownership?.length ? ownership[0].animal?.name : ''}
            </p>
            <p className="animal-info-text">
              {ANIMAL_CONSTANTS.GENDER}: {ownership?.length ? ownership[0].animal?.gender : ''}
            </p>
            <p className="animal-info-text">
              {LABEL_CONSTANTS.QUANTITY_AVAILABLE}: {totalAvailableOwnedQty ?? ''}
            </p>

            {commissionPercent != null && (
              <div className="info-banner">
                <InfoSVG size="18" color="#074B7B" />
                {MARKETPLACE_CONSTANTS.MARKETPLACE_NOTICE.replace('{commission}', String(Number(commissionPercent)))}
              </div>
            )}

            {stripeBlocker === StripeMarketplaceAccess.Setup && (
              <div className="ach-setup-banner">
                <span>{MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_SETUP_REQUIRED_TO_LIST}</span>
                <button
                  type="button"
                  className={accountId == null ? 'button large blue disabled' : 'button large blue'}
                  disabled={accountId == null}
                  onClick={() => {
                    setSetupModalMode(StripePayoutSetupMode.Kyc);
                    setIsAddPaymentMethodModalOpen(true);
                  }}>
                  {BUTTON_CONSTANTS.ADD_PAYMENT_METHOD}
                </button>
              </div>
            )}

            {stripeBlocker === StripeMarketplaceAccess.Document && (
              <div className="ach-setup-banner">
                <span>
                  {stripeStatusGetInfoBanner(StripePayoutBadgeVariant.DocumentDue, stripeStatus)?.message ||
                    MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_DOCUMENT_REQUIRED}
                </span>
                <button
                  type="button"
                  className={accountId == null ? 'button large blue disabled' : 'button large blue'}
                  disabled={accountId == null}
                  onClick={() => setIsDocumentModalOpen(true)}>
                  {LABEL_CONSTANTS.DOCUMENT_UPLOAD}
                </button>
              </div>
            )}

            {stripeBlocker === StripeMarketplaceAccess.Pending && (
              <div className="info-banner">
                <InfoSVG size="18" color="#074B7B" />
                {MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_PENDING_VERIFICATION}
              </div>
            )}

            <div
              className={
                stripeBlocker != null
                  ? 'marketplace-selection-content marketplace-selection-content--locked'
                  : 'marketplace-selection-content'
              }>
              <h2>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h2>
              <div className="desk-select-specimens">
                <div className="specimen-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                        <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                        <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                        {isFemale ? (
                          <>
                            <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                            <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                            <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                          </>
                        ) : (
                          <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                        )}
                        <th>{isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                        <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                        <th>{TABLE_HEADER_CONSTANTS.PRICE_PER_UNIT}</th>
                        <th>{MARKETPLACE_CONSTANTS.TOTAL_LIST_UNIT}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownership
                        ?.filter((item: SpecimenInventory) => {
                          if (isFemale && !item.embryoStrawId) return false;
                          return true;
                        })
                        .map((item: SpecimenInventory, index: number) => {
                          const marketplaceItem = selectedMarketplaceItems[index];
                          const flushMating: string = String(
                            item.specimenCustomDataValues?.find(
                              val => val.specimenCustomDataKey?.keyName === INVENTORY_CONSTANTS.FLUSH_MATING,
                            )?.value ?? '-',
                          );
                          const existingMatch = marketplaceExisting.find(
                            marketPlaceRow => marketPlaceRow.specimenId === marketplaceItem?.specimenId,
                          );

                          return (
                            <tr
                              key={isFemale ? `${item.specimenId}-${item.embryoStrawId}` : item.specimenId}
                              className={handleErrors(marketplaceItem, item)}>
                              <td>{inputCheckbox(marketplaceItem, index)}</td>
                              <td>
                                {item.animal?.code}-{item.animal?.name}
                              </td>
                              <td>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</td>
                              {isFemale ? (
                                <>
                                  <td>
                                    <div>
                                      {item.embryoStrawCode ?? '-'}
                                      <Tooltip>
                                        {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`}

                                        {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                      </Tooltip>
                                    </div>
                                  </td>
                                  <td>{item.embryoStrawNumber ?? '-'}</td>
                                  <td>{flushMating}</td>
                                </>
                              ) : (
                                <td>{item.quality}</td>
                              )}

                              <td>{item.quantityAvailable}</td>
                              <td>{inputQuantityElement(marketplaceItem, index, item)}</td>
                              <td>{inputPriceElement(marketplaceItem, index, existingMatch)}</td>
                              <td>${getMarketplaceItemTotal(marketplaceItem).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      <tr>
                        <td colSpan={isFemale ? TABLE_COLSPAN_BY_GENDER.FEMALE : TABLE_COLSPAN_BY_GENDER.MALE}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/** Mobile View */}
              <div className="xs-select-specimens">
                {ownership
                  ?.filter(item => {
                    if (isFemale && !item.embryoStrawId) return false;
                    return true;
                  })
                  .map((item, index) => {
                    const marketplaceItem = selectedMarketplaceItems[index];
                    const flushMating: string = String(
                      item.specimenCustomDataValues?.find(
                        val => val.specimenCustomDataKey?.keyName === INVENTORY_CONSTANTS.FLUSH_MATING,
                      )?.value ?? '-',
                    );
                    const existingMatch = marketplaceExisting.find(
                      marketPlaceRow => marketPlaceRow.specimenId === marketplaceItem?.specimenId,
                    );

                    return (
                      <div
                        className={`card max-width ${handleErrors(marketplaceItem, item)}`}
                        key={`specimen-row:${item.specimenId}-straw:${item.embryoStrawId ?? 'none'}-index:${index}`}>
                        <div className="selected-specimens-row">
                          <div className="input-check">{inputCheckbox(marketplaceItem, index)}</div>
                          <div className="select-specimens">
                            <div className="card-content-section">
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                                <label>{`${item.animal?.code} - ${item.animal?.name}`}</label>
                              </div>
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                                <label>{<SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} />}</label>
                              </div>
                              <div className="right column">
                                <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                                <label>{flushMating}</label>
                              </div>
                            </div>
                            <div className="card-content-section">
                              {isFemale ? (
                                <>
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                                    <label>
                                      {`${item.embryoStrawCode ?? '-'}`}{' '}
                                      {isFemale && (
                                        <Tooltip>
                                          {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${item.embryoGrade || 'NA'}`}

                                          {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${item.embryoStage || 'NA'}`}
                                        </Tooltip>
                                      )}
                                    </label>
                                  </div>
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                                    <label>{`${item.embryoStrawNumber ?? '-'}`}</label>
                                  </div>
                                </>
                              ) : (
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                                  <label>{`${item.quality}`}</label>
                                </div>
                              )}
                              <div className="left column ">
                                <label>
                                  {`${isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}
                                </label>
                                <label>{`${item.quantityAvailable}`}</label>
                              </div>
                              <div className="left column">
                                <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                                {inputQuantityElement(marketplaceItem, index, item)}
                              </div>
                              <div className="left center column">
                                <label>{`${TABLE_HEADER_CONSTANTS.PRICE_PER_UNIT}:`}</label>
                                <div>{inputPriceElement(marketplaceItem, index, existingMatch)}</div>
                              </div>
                              <div className="right center column">
                                <label>{`${MARKETPLACE_CONSTANTS.TOTAL_LIST_UNIT}:`}</label>
                                <label>${getMarketplaceItemTotal(marketplaceItem).toFixed(2)}</label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="total-summary">
                <div className="summary-section">
                  <div className="summary-label">{LABEL_CONSTANTS.TOTAL_QUANTITY}:</div>
                  <div className="summary-value">{totalMarketplaceQuantity}</div>
                </div>

                <div className="summary-section align-right">
                  <div className="summary-label">{MARKETPLACE_CONSTANTS.TOTAL_PRICE}:</div>
                  <div className="summary-value">${totalMarketplaceValue.toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div className="button-container">
              <button type="button" className="button inverted small red" onClick={returnToInventoryItem}>
                {BUTTON_CONSTANTS.CANCEL}
              </button>
              <button
                type="submit"
                disabled={stripeBlocker != null || !isValid()}
                className={isValid() && stripeBlocker == null ? 'button green large' : 'button green large disabled'}>
                {BUTTON_CONSTANTS.ADD_TO_MARKETPLACE}
              </button>
            </div>
          </form>
        )}
      </div>

      {accountId != null && isAddPaymentMethodModalOpen && (
        <StripePayoutSetupModal
          isOpen={isAddPaymentMethodModalOpen}
          accountId={accountId}
          mode={setupModalMode}
          description={MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_SETUP_REQUIRED_TO_LIST}
          onClose={() => {
            setIsAddPaymentMethodModalOpen(false);
            if (stripeBlocker === StripeMarketplaceAccess.Setup) {
              showToast.error(toastMessages.PAYMENT_DETAILS_REQUIRED);
            }
          }}
          onSuccess={() => setIsAddPaymentMethodModalOpen(false)}
        />
      )}

      <StripePayoutReviewModal isOpen={isPayoutReviewModalOpen} onClose={() => setIsPayoutReviewModalOpen(false)} />

      {accountId != null && isDocumentModalOpen && (
        <StripeKycDocumentModal
          isOpen={isDocumentModalOpen}
          accountId={accountId}
          accountStatus={stripeStatus}
          onClose={() => setIsDocumentModalOpen(false)}
          onSuccess={() => setIsDocumentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default InventoryMarketPlace;
